import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Wallet, Receipt, Coins } from "lucide-react";

// Onboarding flow — three ordered steps (Accounts → Bills → Income) + a
// final review screen. Rewritten Mar 2026 so the sequence mirrors the
// Dashboard's 1/2/3 forecast story (Bank & Cash → Unpaid Bills → Money
// Coming In). Field captions live ABOVE the input rather than as
// placeholder text so mobile users don't lose the label when typing.
const STEPS = ["Welcome", "Accounts", "Bills", "Income", "Ready"];

export default function Onboarding() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  // Modal that pops when the user picks "Explore sample demo data" — spells
  // out exactly what they're about to see + how to remove it later. Prevents
  // the confusion of "why is there data I didn't add?"
  const [showDemoConfirm, setShowDemoConfirm] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  // Each funding source now owns its own balance — no separate "total
  // current balance" number is collected, matching how the app actually
  // stores and displays cash going forward.
  const [fundingSources, setFundingSources] = useState([
    { name: "", type: "checking", balance: "" },
  ]);
  const [bills, setBills] = useState([
    { name: "", due_date: "", estimated_due_amount: "", is_recurring: true, recurrence_frequency: "monthly", status: "unpaid" },
  ]);
  const [incomes, setIncomes] = useState([
    { source: "", amount: "", expected_date: "", frequency: "biweekly" },
  ]);

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const loadDemo = async () => {
    setLoading(true);
    setShowDemoConfirm(false);
    try {
      await api.post("/onboarding/load-demo");
      track("demo_data_loaded", { from: "onboarding" });
      await refresh();
      toast.success("Sample data loaded. Have a look around.");
      // Flag so the Dashboard shows a persistent "you're viewing sample
      // data · Clear it in Settings" banner until the user either clears
      // it or edits every seeded row.
      try { window.localStorage.setItem("wmrb_demo_loaded", "1"); } catch { /* ignore */ }
      // Transient flag so the very next Dashboard load shows a friendlier
      // loading message instead of the generic "Loading your forecast…".
      try { window.sessionStorage.setItem("wmrb_demo_just_loaded", "1"); } catch { /* ignore */ }
      navigate("/app/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const finishSetup = async () => {
    setLoading(true);
    try {
      // Accounts — create each row + set its balance via PATCH.
      for (const fs of fundingSources) {
        if (!fs.name.trim()) continue;
        const { data: created } = await api.post("/funding-sources", {
          name: fs.name, type: fs.type,
          contributes_to_total_balance: true,
        });
        if (fs.balance !== "" && !Number.isNaN(Number(fs.balance))) {
          await api.patch(`/funding-sources/${created.id}`, {
            current_balance: Number(fs.balance),
          });
        }
      }
      for (const b of bills) {
        if (!b.name || !b.due_date || !b.estimated_due_amount) continue;
        await api.post("/bills", {
          name: b.name,
          due_date: b.due_date,
          estimated_due_amount: Number(b.estimated_due_amount),
          is_recurring: !!b.is_recurring,
          recurrence_frequency: b.is_recurring ? b.recurrence_frequency : null,
          status: b.status || "unpaid",
        });
      }
      for (const inc of incomes) {
        if (!inc.source || !inc.amount || !inc.expected_date) continue;
        await api.post("/incomes", {
          source: inc.source,
          amount: Number(inc.amount),
          expected_date: inc.expected_date,
          frequency: inc.frequency,
          status: "expected",
        });
      }
      await api.post("/onboarding/complete");
      // Flag used by the Dashboard to show a one-shot "welcome" note.
      try { window.localStorage.setItem("wmrb_first_dash_visit", "1"); } catch { /* ignore */ }
      track("onboarding_completed");
      await refresh();
      toast.success("You're all set!");
      navigate("/app/dashboard");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#EFE9DE] flex items-start justify-center p-4 sm:p-6" data-testid="onboarding-page">
      <div className="w-full max-w-2xl fade-in">
        <div className="mb-6 flex items-center justify-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-1.5 w-8 rounded-full ${i <= step ? "bg-[#4A6148]" : "bg-[#D8D0BE]"}`} />
          ))}
        </div>

        <div className="card-soft p-6 sm:p-8 md:p-10">
          {step === 0 && (
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#EEF3EC] text-[#4A6148] flex items-center justify-center mb-5">
                <Sparkles size={22} />
              </div>
              <div className="text-xs font-semibold uppercase tracking-widest text-[#8A918D] mb-2">Welcome to</div>
              <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-medium text-[#2C302E] break-words">
                WhatsYourBalance<span className="italic text-[#4A6148]">.com</span>
              </h1>
              <p className="mt-3 text-[#59605C] leading-relaxed">
                In three quick steps, tell us about your money picture and we&rsquo;ll show whether your next paycheck covers what&rsquo;s due. Nothing is ever shared.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <button
                  className="card-nested p-6 text-left"
                  onClick={() => setStep(1)}
                  data-testid="onboarding-start-with-my-data"
                >
                  <div className="font-heading font-medium text-[#2C302E]">Setup my Account</div>
                  <div className="text-sm text-[#59605C] mt-1">Take me to plug in my numbers.</div>
                </button>
                <button
                  className="card-nested p-6 text-left"
                  onClick={() => setShowDemoConfirm(true)}
                  disabled={loading}
                  data-testid="onboarding-load-demo"
                >
                  <div className="font-heading font-medium text-[#2C302E]">Explore sample demo data</div>
                  <div className="text-sm text-[#59605C] mt-1">Play with a realistic month first.</div>
                </button>
              </div>
            </div>
          )}

          {step === 1 && (
            <StepFrame
              icon={<Wallet size={20} />}
              stepLabel="Step 1"
              title="Let's name the accounts you use to pay your bills"
              subtitle="Checking, savings, cash you carry, pay cards — anything you actually spend from. At least one is required."
            >
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] mb-3">Name of accounts you use to pay bills</div>
              {fundingSources.map((fs, i) => (
                <FundingSourceBlock
                  key={i} idx={i} value={fs}
                  onChange={(v) => { const c = [...fundingSources]; c[i] = v; setFundingSources(c); }}
                  onRemove={fundingSources.length > 1 ? () => setFundingSources(fundingSources.filter((_, j) => j !== i)) : null}
                />
              ))}
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setFundingSources([...fundingSources, { name: "", type: "checking", balance: "" }])}
                data-testid="onboarding-add-fs"
              >
                + Add source
              </button>
            </StepFrame>
          )}

          {step === 2 && (
            <StepFrame
              icon={<Receipt size={20} />}
              stepLabel="Step 2"
              title="Your Bills"
              subtitle="Enter two or three bills to get started. You can add the rest from the Bills page later — this just seeds your first forecast."
            >
              {bills.map((b, i) => (
                <BillBlock
                  key={i} idx={i} value={b}
                  onChange={(v) => { const c = [...bills]; c[i] = v; setBills(c); }}
                  onRemove={bills.length > 1 ? () => setBills(bills.filter((_, j) => j !== i)) : null}
                />
              ))}
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setBills([...bills, { name: "", due_date: "", estimated_due_amount: "", is_recurring: true, recurrence_frequency: "monthly", status: "unpaid" }])}
                data-testid="onboarding-add-bill"
              >
                + Add another bill
              </button>
            </StepFrame>
          )}

          {step === 3 && (
            <StepFrame
              icon={<Coins size={20} />}
              stepLabel="Step 3"
              title="Your Income & Pay Dates"
              subtitle="Paychecks and any extras you expect. Set the frequency and the next pay date — we'll project every future paycheck for you."
            >
              {incomes.map((inc, i) => (
                <IncomeBlock
                  key={i} idx={i} value={inc}
                  onChange={(v) => { const c = [...incomes]; c[i] = v; setIncomes(c); }}
                  onRemove={incomes.length > 1 ? () => setIncomes(incomes.filter((_, j) => j !== i)) : null}
                />
              ))}
              <button
                type="button"
                className="btn-ghost text-sm"
                onClick={() => setIncomes([...incomes, { source: "", amount: "", expected_date: "", frequency: "biweekly" }])}
                data-testid="onboarding-add-income"
              >
                + Add another income
              </button>
            </StepFrame>
          )}

          {step === 4 && (
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-[#EEF3EC] text-[#4A6148] flex items-center justify-center mb-5">
                <Sparkles size={22} />
              </div>
              <h1 className="font-heading text-3xl font-medium text-[#2C302E]">Ready to see your forecast</h1>
              <p className="mt-3 text-[#59605C]">
                We&rsquo;ll compute what&rsquo;s due before your next paycheck and tell you if you&rsquo;re Safe, Tight, or Short.
              </p>
              <div className="mt-6 space-y-3 text-left">
                <ReadyBlock
                  label="Step 1 · Accounts"
                  count={fundingSources.filter((f) => f.name.trim()).length}
                  itemLabel="account"
                />
                <ReadyBlock
                  label="Step 2 · Bills"
                  count={bills.filter((b) => b.name && b.due_date && b.estimated_due_amount).length}
                  itemLabel="bill"
                />
                <ReadyBlock
                  label="Step 3 · Income"
                  count={incomes.filter((i) => i.source && i.amount && i.expected_date).length}
                  itemLabel="income source"
                />
              </div>
              <button
                className="btn-primary mt-8"
                onClick={finishSetup}
                disabled={loading}
                data-testid="onboarding-finish-button"
              >
                {loading ? "Saving…" : (<>Enter dashboard <ArrowRight size={16} /></>)}
              </button>
            </div>
          )}

          {step > 0 && step < 4 && (
            <div className="mt-8 flex items-center justify-between">
              <button className="btn-ghost" onClick={goBack} data-testid="onboarding-back-button">Back</button>
              <button className="btn-primary" onClick={goNext} data-testid="onboarding-next-button">
                Next <ArrowRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Sample-data confirm modal — pops when the user picks the demo
          option. Explains WHAT they're about to see + how to remove it
          later, so they never wonder why there's data they didn't add. */}
      {showDemoConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowDemoConfirm(false)}
          data-testid="demo-confirm-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
            data-testid="demo-confirm-modal"
          >
            <div className="w-12 h-12 rounded-full bg-[#EEF3EC] text-[#4A6148] flex items-center justify-center mb-4">
              <Sparkles size={20} />
            </div>
            <h2 className="font-heading text-xl font-semibold text-[#2C302E]">
              About your sample data
            </h2>
            <div className="mt-3 space-y-3 text-sm text-[#59605C] leading-relaxed">
              <p>
                We&rsquo;ll load a made-up household — a couple of accounts, common bills, and paychecks positioned mid-month — so every screen has something to show.
              </p>
              <p>
                <strong className="text-[#2C302E]">When you&rsquo;re ready to enter your own numbers,</strong> head to{" "}
                <span className="font-semibold text-[#4A6148]">Settings → Clear sample data</span>. That removes any demo rows you haven&rsquo;t touched. Anything you&rsquo;ve edited or added is treated as yours and stays safe.
              </p>
              <p className="text-xs text-[#8A918D]">
                Tip: you can also just start editing the demo rows — the first edit claims that row as yours.
              </p>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setShowDemoConfirm(false)}
                data-testid="demo-confirm-cancel"
              >
                Nevermind
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={loadDemo}
                disabled={loading}
                data-testid="demo-confirm-load"
              >
                {loading ? "Loading…" : "Load sample data"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────── Layout helpers ───────────
function StepFrame({ icon, stepLabel, title, subtitle, children }) {
  return (
    <div>
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-[#EEF3EC] text-[#4A6148] flex items-center justify-center shrink-0">{icon}</div>
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#4A6148]">{stepLabel}</div>
          <h2 className="font-heading text-xl md:text-2xl font-medium text-[#2C302E] leading-tight">{title}</h2>
          {subtitle && <p className="text-sm text-[#59605C] mt-1 leading-relaxed">{subtitle}</p>}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function FieldLabel({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="block text-[10px] font-semibold uppercase tracking-widest text-[#59605C] mb-1">
      {children}
    </label>
  );
}

// ─────────── Row cards (rectangular outlines per user request) ───────────
function CardShell({ children, onRemove, testId }) {
  return (
    <div className="border border-[#D8CFB9] rounded-2xl p-4 mb-3 bg-[#FBFAF6] relative" data-testid={testId}>
      {onRemove && (
        <button
          type="button"
          className="absolute top-2 right-2 text-[#8A918D] hover:text-[#8C3A30] text-lg leading-none"
          onClick={onRemove}
          aria-label="Remove"
          data-testid={`${testId}-remove`}
        >×</button>
      )}
      {children}
    </div>
  );
}

function FundingSourceBlock({ idx, value, onChange, onRemove }) {
  return (
    <CardShell onRemove={onRemove} testId={`onboarding-fs-${idx}`}>
      {/* Type on the left, Name on the right — matches user's mobile spec */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor={`fs-type-${idx}`}>Type</FieldLabel>
          <select
            id={`fs-type-${idx}`}
            className="input-soft"
            value={value.type}
            onChange={(e) => onChange({ ...value, type: e.target.value })}
            data-testid={`onboarding-fs-type-${idx}`}
          >
            <option value="checking">Checking</option>
            <option value="savings">Savings</option>
            <option value="cash">Cash</option>
            <option value="pay_card">Paycheck card</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`fs-name-${idx}`}>Account name</FieldLabel>
          <input
            id={`fs-name-${idx}`}
            className="input-soft"
            placeholder="e.g. Checking 2314"
            value={value.name}
            onChange={(e) => onChange({ ...value, name: e.target.value })}
            data-testid={`onboarding-fs-name-${idx}`}
          />
        </div>
      </div>
      {/* Current Balance — label on left, amount on right (sits nicely on both mobile + desktop) */}
      <div className="mt-3 flex items-center gap-3">
        <FieldLabel htmlFor={`fs-bal-${idx}`}><span className="whitespace-nowrap">Current balance</span></FieldLabel>
        <input
          id={`fs-bal-${idx}`}
          className="input-soft flex-1 !mb-0"
          type="number" step="0.01" inputMode="decimal"
          placeholder="0.00"
          value={value.balance}
          onChange={(e) => onChange({ ...value, balance: e.target.value })}
          data-testid={`onboarding-fs-balance-${idx}`}
        />
      </div>
    </CardShell>
  );
}

function BillBlock({ idx, value, onChange, onRemove }) {
  return (
    <CardShell onRemove={onRemove} testId={`onboarding-bill-${idx}`}>
      {/* Bill name — full width so it stops fighting the amount field on mobile. */}
      <FieldLabel htmlFor={`bill-name-${idx}`}>Bill name</FieldLabel>
      <input
        id={`bill-name-${idx}`}
        className="input-soft"
        placeholder="e.g. Rent"
        value={value.name}
        onChange={(e) => onChange({ ...value, name: e.target.value })}
        data-testid={`onboarding-bill-name-${idx}`}
      />
      {/* Amount — dedicated row with plenty of space. */}
      <div className="mt-3">
        <FieldLabel htmlFor={`bill-amount-${idx}`}>Estimated amount</FieldLabel>
        <input
          id={`bill-amount-${idx}`}
          className="input-soft"
          type="number" step="0.01" inputMode="decimal"
          placeholder="e.g. 1200"
          value={value.estimated_due_amount}
          onChange={(e) => onChange({ ...value, estimated_due_amount: e.target.value })}
          data-testid={`onboarding-bill-amount-${idx}`}
        />
        <div className="text-[11px] text-[#8A918D] mt-1">You can update it later.</div>
      </div>
      <div className="mt-3">
        <FieldLabel htmlFor={`bill-date-${idx}`}>Due date</FieldLabel>
        <input
          id={`bill-date-${idx}`}
          className="input-soft"
          type="date"
          value={value.due_date}
          onChange={(e) => onChange({ ...value, due_date: e.target.value })}
          data-testid={`onboarding-bill-date-${idx}`}
        />
      </div>
      {/* Frequency + Status on the same row for compactness on desktop, stacks on mobile. */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor={`bill-freq-${idx}`}>Recurring?</FieldLabel>
          <select
            id={`bill-freq-${idx}`}
            className="input-soft"
            value={value.is_recurring ? value.recurrence_frequency : "once"}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "once") onChange({ ...value, is_recurring: false, recurrence_frequency: null });
              else onChange({ ...value, is_recurring: true, recurrence_frequency: v });
            }}
            data-testid={`onboarding-bill-freq-${idx}`}
          >
            <option value="once">One-time</option>
            <option value="weekly">Recurring · weekly</option>
            <option value="biweekly">Recurring · biweekly</option>
            <option value="monthly">Recurring · monthly</option>
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`bill-status-${idx}`}>Status</FieldLabel>
          <select
            id={`bill-status-${idx}`}
            className="input-soft"
            value={value.status || "unpaid"}
            onChange={(e) => onChange({ ...value, status: e.target.value })}
            data-testid={`onboarding-bill-status-${idx}`}
          >
            <option value="unpaid">Unpaid</option>
            <option value="pending">Pending (paid, waiting to clear)</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>
    </CardShell>
  );
}

function IncomeBlock({ idx, value, onChange, onRemove }) {
  return (
    <CardShell onRemove={onRemove} testId={`onboarding-income-${idx}`}>
      <FieldLabel htmlFor={`inc-source-${idx}`}>Income name</FieldLabel>
      <input
        id={`inc-source-${idx}`}
        className="input-soft"
        placeholder="e.g. Employer Paycheck"
        value={value.source}
        onChange={(e) => onChange({ ...value, source: e.target.value })}
        data-testid={`onboarding-income-source-${idx}`}
      />
      <div className="mt-3">
        <FieldLabel htmlFor={`inc-amount-${idx}`}>Amount per paycheck</FieldLabel>
        <input
          id={`inc-amount-${idx}`}
          className="input-soft"
          type="number" step="0.01" inputMode="decimal"
          placeholder="e.g. 2500"
          value={value.amount}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          data-testid={`onboarding-income-amount-${idx}`}
        />
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <FieldLabel htmlFor={`inc-freq-${idx}`}>Frequency</FieldLabel>
          <select
            id={`inc-freq-${idx}`}
            className="input-soft"
            value={value.frequency}
            onChange={(e) => onChange({ ...value, frequency: e.target.value })}
            data-testid={`onboarding-income-frequency-${idx}`}
          >
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="monthly">Monthly</option>
            <option value="unscheduled">One-time / Unscheduled</option>
          </select>
        </div>
        <div>
          <FieldLabel htmlFor={`inc-date-${idx}`}>Next pay date</FieldLabel>
          <input
            id={`inc-date-${idx}`}
            className="input-soft"
            type="date"
            value={value.expected_date}
            onChange={(e) => onChange({ ...value, expected_date: e.target.value })}
            data-testid={`onboarding-income-date-${idx}`}
          />
        </div>
      </div>
    </CardShell>
  );
}

function ReadyBlock({ label, count, itemLabel }) {
  return (
    <div className="border border-[#D8CFB9] rounded-2xl p-4 bg-[#FBFAF6] flex items-center justify-between">
      <div className="text-[11px] font-semibold uppercase tracking-widest text-[#4A6148]">{label}</div>
      <div className="font-heading text-lg text-[#2C302E]">
        {count} {itemLabel}{count === 1 ? "" : "s"}
      </div>
    </div>
  );
}
